import { t } from '@lingui/macro'
import * as React from 'react'
import styled, { useTheme } from 'styled-components'

import { FlexColumn, FlexRow } from '@/@bothrs-ui/web/components/layout'
import { Markdown } from '@/@bothrs-ui/web/components/markdown/Markdown'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { formatLocale } from '@/modules/shared/helpers/locale'
import { MessageAttachmentItem } from '../../message-attachment-item/MessageAttachmentItem'

import type { PatientCommunicationMessage as PatientCommunicationMessageType } from '../../../types/message.types'

export type PatientCommunicationMessageProps = {
  message: PatientCommunicationMessageType
  onAttachmentOpen?: () => void
}

export function PatientCommunicationMessage({
  message: { sender, content, createdAt, id, attachments = [] },
  onAttachmentOpen,
}: PatientCommunicationMessageProps) {
  const theme = useTheme()

  const senderInfo = React.useMemo(() => {
    if (sender.type === 'patient') {
      return {
        name: sender.name,
      }
    }

    if (sender.doctorInfo?.name) {
      return {
        name: sender.doctorInfo.name,
      }
    }

    if (sender.specialisationInfo?.title?.nl) {
      return {
        name: t({
          id: 'globals.medical-team.service.prefix',
          message: 'Dienst {specialisation}',
          values: {
            specialisation: sender.specialisationInfo.title.nl,
          },
          comment: 'Prefix for medical team service',
        }),
      }
    }

    return {
      name: '',
    }
  }, [sender])

  const timeStamps = React.useMemo(() => {
    const time = formatLocale(createdAt, 'HH:mm')
    const date = formatLocale(createdAt, 'dd/MM/yyyy')

    return {
      time,
      date,
    }
  }, [createdAt])

  return (
    <StMessageContent key={id} $isPatient={sender.type === 'patient'}>
      {/* Content */}
      <StContent $isPatient={sender.type === 'patient'}>
        <Markdown content={content} />
      </StContent>

      {/* Attachments */}
      {attachments?.length > 0 && (
        <FlexColumn gap={theme.UI.Spacing.Space2}>
          {attachments.map((attachment) => (
            <MessageAttachmentItem
              key={attachment.name}
              attachment={attachment}
              source='communication'
              onAttachmentOpen={onAttachmentOpen}
            />
          ))}
        </FlexColumn>
      )}

      <StTimeContainer $isPatient={sender.type === 'patient'}>
        <Span type='BodySmallRegular' color={theme.Colors.Neutrals[400]}>
          {senderInfo.name}
        </Span>
        <Span type='BodySmallRegular' color={theme.Colors.Neutrals[400]}>
          {senderInfo.name ? '·' : ''}
        </Span>
        <Span type='BodySmallRegular' color={theme.Colors.Neutrals[400]}>
          {timeStamps.date}
        </Span>
        <Span type='BodySmallRegular' color={theme.Colors.Neutrals[400]}>
          ·
        </Span>
        <Span type='BodySmallRegular' color={theme.Colors.Neutrals[400]}>
          {timeStamps.time}
        </Span>
      </StTimeContainer>
    </StMessageContent>
  )
}

const StMessageContent = styled(FlexColumn)<{ $isPatient: boolean }>`
  width: 90%;
  margin-left: ${({ $isPatient }) => ($isPatient ? 'auto' : '0')};
  gap: ${({ theme }) => theme.UI.Spacing.Space2}px;
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
  margin-bottom: ${({ theme }) => theme.UI.Spacing.Space8}px;
`
const StContent = styled.div<{ $isPatient: boolean }>`
  background-color: ${({ $isPatient, theme }) => ($isPatient ? '#F0F3F8' : theme.Colors.Background.White)};
  padding: ${({ $isPatient, theme }) => ($isPatient ? `${theme.UI.SpacingPx.Space2} ${theme.UI.SpacingPx.Space4}` : 0)};
  border-radius: ${({ $isPatient }) => ($isPatient ? 8 : 0)}px;
`

const StTimeContainer = styled(FlexRow)<{ $isPatient: boolean }>`
  gap: ${({ theme }) => theme.UI.SpacingPx.Space1};
  flex-wrap: wrap;
  justify-content: ${({ $isPatient }) => ($isPatient ? 'flex-end' : 'flex-start')};
`
